import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import { toast } from 'react-toastify';
import { DASHBOARD_ROUTES } from 'Routes';
import { useAppDispatch } from 'store/hooks';
import { refreshConverstationAsync } from 'store/intercomSlice';
import { saveUser } from 'store/userSlide';
import { saveNavigateLinkHeader } from 'store/navigateSlide';
import { selectAccount } from 'store/accountSlide';
import {
  deescalateConversation,
  escalateConversation,
  getInboxAssignees,
  getInboxConversations,
  getInboxCounts,
  InboxAssignee,
  InboxConversation,
  InboxMemberUpdateRequest,
  IntercomSyncSummary,
  updateMemberConversation,
} from 'data/api/conversation.api';
import { saveInboxCounts, selectInboxCounts } from 'store/inboxSlice';
import { ConversationThread } from 'components/conversation/ConversationThread';
import { ReplyComposer } from 'components/conversation/ReplyComposer';
import { useDraftReply } from 'components/conversation/useDraftReply';
import { SnoozeFailure } from 'components/conversation/SnoozeButton';
import { InboxQueue, QueueTab } from './components/InboxQueue';
import { ContextRail } from './components/ContextRail';
import { SyncFreshness } from './components/SyncFreshness';
import { useMemberTriages } from './useMemberTriages';
import { useMemberStatusHistory } from './useMemberStatusHistory';

const PAGE_SIZE = 25;
// The backend's own cap on one page.
const MAX_PAGE_SIZE = 100;
// The backend reads support@ and syncs Intercom about once a minute, so a
// half-minute poll shows a new reply within a minute or so of it landing.
const REFRESH_MS = 30 * 1000;
// The open family's triages and thread are quick reads and are what a rep is
// waiting on. The queue and its counts are among the slowest reads in prod
// (seconds per call, 10/2026), so they refresh on every fourth tick only.
const QUEUE_EVERY_TICKS = 4;

// Mirrors Sidebar.tsx's w-[64px]. Not the same 64 as the height calc below,
// which is the header — they are unrelated dimensions that happen to match.
const SIDEBAR_WIDTH = '64px';

// The api interceptor rejects with the backend's message string, not the
// response, so the status is gone by the time it reaches a catch. Nest sends
// "Forbidden resource" (RolesGuard) or "Forbidden" (service gate) on a 403 —
// the only signal we have that retrying is pointless. De-escalate is
// PlatformAdmin-only, so care partners land here.
// Case-insensitive: conversation.api.ts's rethrow upper-cases string errors,
// so the message arrives as "FORBIDDEN RESOURCE".
const errorText = (error: unknown) =>
  typeof error === 'string' ? error : (error as Error)?.message;

const permissionMessage = (error: unknown, fallback: string) =>
  errorText(error)?.toUpperCase().startsWith('FORBIDDEN')
    ? 'You do not have permission to do that.'
    : fallback;

// The backend's 400 for a snooze time it refuses carries exactly this, and
// only that failure is something a different time can fix. Compared
// case-insensitively for the same upper-casing reason as above.
const SNOOZE_TIME_INVALID =
  'Snooze time must be in the future and within 90 days.';

const snoozeFailure = (error: unknown): SnoozeFailure =>
  errorText(error)?.toUpperCase() === SNOOZE_TIME_INVALID.toUpperCase()
    ? { message: SNOOZE_TIME_INVALID, invalidTime: true }
    : {
        message: permissionMessage(
          error,
          'Couldn’t snooze this conversation. Try again.',
        ),
        invalidTime: false,
      };

const CompanyName: React.FC<{
  companyId?: string;
  name?: string;
  linked: boolean;
}> = ({ companyId, name, linked }) => {
  if (!name) return null;
  const sx = { fontSize: '14px', color: '#7D818C', textAlign: 'right' };
  return linked && companyId ? (
    <Link
      href={DASHBOARD_ROUTES.COMPANY_DETAIL + companyId}
      underline="hover"
      sx={sx}
    >
      {name}
    </Link>
  ) : (
    <Typography sx={sx}>{name}</Typography>
  );
};

export const InboxPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { memberUserId: linkedMemberId } = useParams();
  const account = useSelector(selectAccount);
  // Mirrors the backend gate: platform admins only. Undefined while the
  // account is still loading, so the control stays hidden until we know.
  const canDeescalate = !!account?.isPlatformAdmin;
  // Server-owned: GET /users answers this from the same policy the summary
  // route authorizes on, kill switch included. Undefined while the account
  // loads, which reads as off, so the section stays hidden until we know
  // rather than appearing and then vanishing.
  const canSummarize = !!account?.canSummarize;
  // Company pages are platform-admin screens; a care partner gets the names
  // without links rather than a link that 403s.
  const canOpenCompanies = !!account?.isPlatformAdmin;

  const [conversations, setConversations] = useState<InboxConversation[]>([]);
  const [selectedMemberId, setSelectedMemberId] = useState<string>();
  const [updatingIds, setUpdatingIds] = useState<Set<string>>(new Set());
  const [searchResetKey] = useState<number>(0);
  const [refreshToken, setRefreshToken] = useState<number>(0);
  // Guards against a slow, superseded list request overwriting newer results.
  const requestSeqRef = useRef(0);
  const assigneesLoadedRef = useRef(false);
  const assigneesInFlightRef = useRef(false);
  // Bumped by every row update, so a background refresh that read the list
  // before the update can't paint the old assignment back over it.
  const mutationSeqRef = useRef(0);
  const refreshingRef = useRef(false);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [intercomSync, setIntercomSync] = useState<IntercomSyncSummary | null>(
    null,
  );
  const [assignees, setAssignees] = useState<InboxAssignee[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<boolean>(false);
  const [tab, setTab] = useState<QueueTab>('open');
  const [query, setQuery] = useState<string>();
  const [escalateTarget, setEscalateTarget] =
    useState<InboxConversation | null>(null);
  // Which triage's detail the rail is showing. Ids are unique, so a selection
  // left over from another family simply matches nothing once the list
  // changes — there is no reset to forget.
  const [openTriageId, setOpenTriageId] = useState<string>();
  const [escalateReason, setEscalateReason] = useState<string>('');
  const counts = useSelector(selectInboxCounts);
  // A shared link can name a family that is not on the page the queue loads:
  // closed, or further down. It is fetched on its own and slotted into the
  // next list load for its tab, once.
  const pinnedRef = useRef<InboxConversation>();
  const [openingLink, setOpeningLink] = useState<boolean>(!!linkedMemberId);
  const openingTokenRef = useRef(0);
  // Bumped whenever the user moves on (another row, tab or search), so a link
  // lookup still in flight is dropped instead of yanking the thread back.
  const selectionSeqRef = useRef(0);
  // The route's id as of now, for callbacks created on an earlier render.
  const routeIdRef = useRef(linkedMemberId);
  routeIdRef.current = linkedMemberId;

  const selected =
    conversations.find(
      (conversation) => conversation.memberUserId === selectedMemberId,
    ) ??
    (pinnedRef.current?.memberUserId === selectedMemberId
      ? pinnedRef.current
      : undefined);
  const reply = useDraftReply(selected?.memberUserId);
  // One triage read per selected member, shared by the rail's Triages section
  // and the inline events in the thread.
  const triages = useMemberTriages(selected?.memberUserId);
  const statusHistory = useMemberStatusHistory(selected?.memberUserId);

  const fetchConversations = async (nextPage: number, append: boolean) => {
    const seq = ++requestSeqRef.current;
    if (append) setLoadingMore(true);
    else setLoading(true);
    try {
      const response = await getInboxConversations({
        page: nextPage,
        pageSize: PAGE_SIZE,
        order: 'desc',
        status: tab,
        assignedTo: 'all',
        escalated: 'all',
        query: query,
      });
      if (seq !== requestSeqRef.current) return;
      let incoming = response.conversations || [];
      const pinned = pinnedRef.current;
      if (!append && pinned && pinned.inboxStatus === tab) {
        pinnedRef.current = undefined;
        if (!incoming.some((c) => c.memberUserId === pinned.memberUserId)) {
          incoming = [pinned, ...incoming];
        }
      }
      setConversations((current) => {
        if (!append) return incoming;
        // The queue reorders as messages arrive, so offset pages can re-serve
        // a member already on screen; keys are memberUserId, so a duplicate
        // is a rendering bug, not just a cosmetic one.
        const seen = new Set(current.map((c) => c.memberUserId));
        return [
          ...current,
          ...incoming.filter((c) => !seen.has(c.memberUserId)),
        ];
      });
      setTotal(response.pager?.total || 0);
      setPage(nextPage);
      setIntercomSync(response.intercomSync ?? null);
      setLoadError(false);
    } catch (error) {
      if (seq !== requestSeqRef.current) return;
      console.log(error);
      if (!append) {
        setConversations([]);
        setTotal(0);
      }
      setLoadError(true);
      toast.error('Could not load the inbox. Please try again.');
    }
    setLoading(false);
    setLoadingMore(false);
  };

  const fetchAssignees = async () => {
    if (assigneesInFlightRef.current) return;
    assigneesInFlightRef.current = true;
    try {
      setAssignees(await getInboxAssignees());
      assigneesLoadedRef.current = true;
    } catch (error) {
      console.log(error);
    }
    assigneesInFlightRef.current = false;
  };

  // Retry a failed staff load on the next interaction instead of leaving the
  // assignment dropdown unusable until an unrelated refetch happens to run.
  const retryAssigneesIfNeeded = () => {
    if (!assigneesLoadedRef.current) void fetchAssignees();
  };

  const fetchCounts = async () => {
    // Shared via the inbox slice so the sidebar badge follows along; the
    // slice drops responses older than what it already holds.
    const requestedAt = Date.now();
    try {
      const nextCounts = await getInboxCounts();
      dispatch(saveInboxCounts({ counts: nextCounts, requestedAt }));
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    void fetchConversations(0, false);
    void fetchCounts();
    if (!assigneesLoadedRef.current) void fetchAssignees();
  }, [tab, query, refreshToken]);

  // Re-reads what is on screen so new messages, triage replies and other
  // staff's changes show up without a reload. Nothing spins, and nothing is
  // replaced unless it changed. Stands aside while the user has a load or an
  // update in flight; those refetch what they touch themselves.
  const refreshInbox = async (withQueue: boolean) => {
    if (
      loading ||
      loadingMore ||
      loadError ||
      openingLink ||
      updatingIds.size
    ) {
      return;
    }
    triages.refresh();
    statusHistory.refresh();
    if (selected)
      void dispatch(refreshConverstationAsync(selected.memberUserId));
    if (!withQueue || refreshingRef.current) return;
    refreshingRef.current = true;
    void fetchCounts();
    const seq = requestSeqRef.current;
    const mutation = mutationSeqRef.current;
    const selection = selectionSeqRef.current;
    const pageSize = Math.min(
      MAX_PAGE_SIZE,
      Math.max(PAGE_SIZE, conversations.length),
    );
    try {
      const response = await getInboxConversations({
        page: 0,
        pageSize,
        order: 'desc',
        status: tab,
        assignedTo: 'all',
        escalated: 'all',
        query: query,
      });
      if (
        seq !== requestSeqRef.current ||
        mutation !== mutationSeqRef.current ||
        selection !== selectionSeqRef.current
      ) {
        return;
      }
      setConversations((current) => {
        const incoming = response.conversations || [];
        const seen = new Set(incoming.map((c) => c.memberUserId));
        // Past the backend's page cap, rows loaded further down stay as
        // they are until the next deliberate load.
        const next = [
          ...incoming,
          ...current.slice(pageSize).filter((c) => !seen.has(c.memberUserId)),
        ];
        // The family being read stays where it is even if someone else
        // closed it meanwhile; dropping it would switch the thread out from
        // under a reply in progress.
        const openIndex = current.findIndex(
          (c) => c.memberUserId === selectedMemberId,
        );
        if (
          openIndex >= 0 &&
          !next.some((c) => c.memberUserId === selectedMemberId)
        ) {
          next.splice(Math.min(openIndex, next.length), 0, current[openIndex]);
        }
        return JSON.stringify(next) === JSON.stringify(current)
          ? current
          : next;
      });
      setTotal(response.pager?.total || 0);
      setIntercomSync(response.intercomSync ?? null);
    } catch (error) {
      // Keeps what is on screen; the next tick tries again.
      console.log(error);
    } finally {
      refreshingRef.current = false;
    }
  };

  // Callbacks close over the render they were made in, so the timer calls
  // through a ref to always reach the current one.
  const refreshRef = useRef(refreshInbox);
  refreshRef.current = refreshInbox;
  useEffect(() => {
    let ticks = 0;
    const run = (withQueue: boolean) => {
      if (document.visibilityState === 'visible') {
        void refreshRef.current(withQueue);
      }
    };
    const timer = window.setInterval(() => {
      ticks += 1;
      run(ticks % QUEUE_EVERY_TICKS === 0);
    }, REFRESH_MS);
    // Coming back to the tab catches up at once instead of on the next tick.
    const onVisible = () => run(true);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  // Keep a selection only while it is still in the list the queue is showing.
  // A link being opened owns the selection until it lands.
  useEffect(() => {
    if (openingLink) return;
    // The linked family is selected and on its way into the list.
    const pinned = pinnedRef.current;
    if (pinned && pinned.memberUserId === selectedMemberId) return;
    if (!conversations.length) {
      setSelectedMemberId(undefined);
      if (routeIdRef.current) {
        navigate(DASHBOARD_ROUTES.ROUTE_INBOX, { replace: true });
      }
      return;
    }
    const stillPresent = conversations.some(
      (conversation) => conversation.memberUserId === selectedMemberId,
    );
    if (!stillPresent) handleSelect(conversations[0]);
  }, [conversations, openingLink]);

  useEffect(() => {
    if (!linkedMemberId || linkedMemberId === selectedMemberId) return;
    const loaded = conversations.find(
      (conversation) => conversation.memberUserId === linkedMemberId,
    );
    if (loaded) handleSelect(loaded);
    else void openLink(linkedMemberId);
  }, [linkedMemberId]);

  const openLink = async (memberUserId: string) => {
    const token = ++selectionSeqRef.current;
    openingTokenRef.current = token;
    setOpeningLink(true);
    try {
      const response = await getInboxConversations({
        page: 0,
        pageSize: 1,
        status: 'all',
        assignedTo: 'all',
        escalated: 'all',
        memberUserId,
      });
      if (token !== selectionSeqRef.current) return;
      const linked = response.conversations?.[0];
      if (!linked) {
        toast.error('That conversation could not be found.');
        navigate(DASHBOARD_ROUTES.ROUTE_INBOX, { replace: true });
        return;
      }
      pinnedRef.current = linked;
      handleSelect(linked);
      if (linked.inboxStatus === tab) setRefreshToken((token) => token + 1);
      else setTab(linked.inboxStatus);
    } catch (error) {
      if (token !== selectionSeqRef.current) return;
      console.log(error);
      toast.error('Could not open that conversation. Please try again.');
      navigate(DASHBOARD_ROUTES.ROUTE_INBOX, { replace: true });
    } finally {
      if (openingTokenRef.current === token) setOpeningLink(false);
    }
  };

  const moveOn = () => {
    selectionSeqRef.current += 1;
    pinnedRef.current = undefined;
  };

  const copyLink = async (memberUserId: string) => {
    const url =
      window.location.origin +
      DASHBOARD_ROUTES.INBOX_CONVERSATION(memberUserId);
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied');
    } catch (error) {
      console.log(error);
      toast.error('Could not copy the link.');
    }
  };

  function handleSelect(conversation: InboxConversation) {
    setSelectedMemberId(conversation.memberUserId);
    // The URL names the open conversation, so it can be shared as-is.
    if (conversation.memberUserId !== routeIdRef.current) {
      navigate(DASHBOARD_ROUTES.INBOX_CONVERSATION(conversation.memberUserId), {
        replace: true,
      });
    }
    // Downstream pages (notes, member detail) read the member off the store.
    dispatch(
      saveUser({
        userId: conversation.memberUserId,
        firstName: conversation.memberFirstName,
        lastName: conversation.memberLastName,
        companyName: conversation.companyName,
      }),
    );
  }

  const withRowBusy = async (
    memberUserId: string,
    work: () => Promise<void>,
  ) => {
    // One in-flight update per family; the row controls are disabled
    // meanwhile, so responses cannot land out of order.
    if (updatingIds.has(memberUserId)) return;
    mutationSeqRef.current += 1;
    setUpdatingIds((current) => new Set(current).add(memberUserId));
    try {
      await work();
    } finally {
      setUpdatingIds((current) => {
        const next = new Set(current);
        next.delete(memberUserId);
        return next;
      });
    }
  };

  /** Resolves to what the update failed with, else null. */
  const applyUpdate = async (
    memberUserId: string,
    body: InboxMemberUpdateRequest,
  ): Promise<{ error: unknown } | null> => {
    let failure: { error: unknown } | null = null;
    await withRowBusy(memberUserId, async () => {
      try {
        const updated = await updateMemberConversation(memberUserId, body);
        setConversations((current) =>
          current.map((conversation) =>
            conversation.memberUserId === memberUserId
              ? {
                  ...conversation,
                  inboxStatus: updated.inboxStatus,
                  snoozedUntil:
                    updated.inboxStatus === 'snoozed'
                      ? updated.snoozedUntil ?? body.snoozedUntil
                      : null,
                  // The write normalised every segment, so a split is gone.
                  splitAssignment: false,
                  assignedToUserId: updated.assignedToUserId || undefined,
                  assignedToName:
                    updated.assignedToUserId != null
                      ? updated.assignedToName ||
                        assignees.find(
                          (a) => a.userId === updated.assignedToUserId,
                        )?.name
                      : undefined,
                }
              : conversation,
          ),
        );
        // A status change moves the row out of the active tab, so refetch on
        // current state rather than through this (possibly stale) closure.
        // The thread's markers follow: the write logged a history row.
        if (body.status) {
          setRefreshToken((token) => token + 1);
          statusHistory.refresh();
        } else void fetchCounts();
      } catch (error) {
        console.log(error);
        failure = { error };
      }
    });
    return failure;
  };

  const updateOrToast = (
    memberUserId: string,
    body: InboxMemberUpdateRequest,
  ) =>
    void applyUpdate(memberUserId, body).then(
      (failure) =>
        failure &&
        toast.error(
          permissionMessage(
            failure.error,
            'Could not update the conversation. Please try again.',
          ),
        ),
    );

  // The snooze control shows these itself: inline when the time was refused.
  const snooze = async (
    memberUserId: string,
    until: string,
  ): Promise<SnoozeFailure | null> => {
    const failure = await applyUpdate(memberUserId, {
      status: 'snoozed',
      snoozedUntil: until,
    });
    return failure && snoozeFailure(failure.error);
  };

  const applyEscalation = (
    memberUserId: string,
    action: 'escalate' | 'deescalate',
    reason?: string,
  ) =>
    withRowBusy(memberUserId, async () => {
      try {
        const updated =
          action === 'escalate'
            ? await escalateConversation(memberUserId, reason || '')
            : await deescalateConversation(memberUserId);
        setConversations((current) =>
          current.map((conversation) =>
            conversation.memberUserId === memberUserId
              ? {
                  ...conversation,
                  // The server returns the resulting state, not a timestamp;
                  // refetch below settles the detail.
                  escalatedAt: updated.escalated
                    ? conversation.escalatedAt || new Date().toISOString()
                    : undefined,
                  escalationReason: updated.escalated
                    ? reason || conversation.escalationReason
                    : undefined,
                }
              : conversation,
          ),
        );
        setRefreshToken((token) => token + 1);
        void fetchCounts();
      } catch (error) {
        console.log(error);
        toast.error(
          permissionMessage(
            error,
            'Could not update the escalation. Please try again.',
          ),
        );
      }
    });

  const confirmEscalate = () => {
    if (!escalateTarget) return;
    void applyEscalation(
      escalateTarget.memberUserId,
      'escalate',
      escalateReason.trim(),
    );
    setEscalateTarget(null);
    setEscalateReason('');
  };

  const openNotes = (conversation: InboxConversation) => {
    dispatch(
      saveNavigateLinkHeader({
        // Plain /inbox would re-select the first row on the way back.
        fromLink: DASHBOARD_ROUTES.INBOX_CONVERSATION(
          conversation.memberUserId,
        ),
        toLink: DASHBOARD_ROUTES.USER_ID_NOTES(conversation.memberUserId),
      }),
    );
    navigate(DASHBOARD_ROUTES.USER_ID_NOTES(conversation.memberUserId));
  };

  const selectedBusy = !!selected && updatingIds.has(selected.memberUserId);

  return (
    <>
      <Stack
        direction="row"
        sx={{
          height: 'calc(100vh - 64px)',
          minHeight: 0,
          // The sidebar is position:fixed, so it paints over anything starting
          // at x=0. Other pages clear it via .nv-container's 140px gutter,
          // which would break a full-bleed three-pane shell — so match the
          // sidebar's own width instead. Keep in step with Sidebar.tsx's
          // w-[64px]. The offset comes out of the thread column (flex: 1);
          // the queue and rail keep their fixed widths.
          marginLeft: SIDEBAR_WIDTH,
          backgroundColor: '#FFFFFF',
          borderTop: '1px solid #EFEDEB',
        }}
      >
        <InboxQueue
          conversations={conversations}
          counts={counts}
          closedCount={tab === 'closed' ? total : undefined}
          tab={tab}
          onTabChange={(next) => {
            moveOn();
            setTab(next);
            setConversations([]);
          }}
          selectedMemberId={selectedMemberId}
          updatingIds={updatingIds}
          loading={loading}
          loadError={loadError}
          searchResetKey={searchResetKey}
          hasMore={conversations.length < total}
          loadingMore={loadingMore}
          onSearch={(value) => {
            moveOn();
            setQuery(value || undefined);
          }}
          onSelect={(conversation) => {
            moveOn();
            handleSelect(conversation);
          }}
          onToggleStatus={(conversation) =>
            // Snoozed and closed rows both offer Reopen.
            updateOrToast(conversation.memberUserId, {
              status: conversation.inboxStatus === 'open' ? 'closed' : 'open',
            })
          }
          onSnooze={(conversation, until) =>
            snooze(conversation.memberUserId, until)
          }
          onLoadMore={() => void fetchConversations(page + 1, true)}
          onRetry={() => void fetchConversations(0, false)}
        />

        <Stack sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
          {!selected ? (
            <Stack
              alignItems="center"
              justifyContent="center"
              spacing={1}
              sx={{ flex: 1 }}
            >
              <ForumOutlinedIcon sx={{ color: '#7D818C', fontSize: '36px' }} />
              <Typography
                sx={{ color: '#213B54', fontWeight: 600, fontSize: '15px' }}
              >
                Select a conversation
              </Typography>
            </Stack>
          ) : (
            <>
              <Stack
                sx={{
                  padding: '14px 20px',
                  borderBottom: '1px solid #EFEDEB',
                  gap: '2px',
                }}
              >
                <Stack
                  direction="row"
                  alignItems="flex-start"
                  justifyContent="space-between"
                  gap={2}
                >
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <Typography
                      sx={{
                        fontSize: '20px',
                        fontWeight: 700,
                        color: '#213B54',
                      }}
                    >
                      {selected.memberName}
                    </Typography>
                    <Tooltip title="Copy link to this conversation">
                      <IconButton
                        size="small"
                        aria-label="Copy link to this conversation"
                        onClick={() => void copyLink(selected.memberUserId)}
                        sx={{ color: '#7D818C' }}
                      >
                        <LinkRoundedIcon sx={{ fontSize: '20px' }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  <Stack alignItems="flex-end" sx={{ paddingTop: '4px' }}>
                    <CompanyName
                      companyId={selected.companyId}
                      name={selected.companyName}
                      linked={canOpenCompanies}
                    />
                    <CompanyName
                      companyId={selected.parentCompanyId}
                      name={selected.parentCompanyName}
                      linked={canOpenCompanies}
                    />
                  </Stack>
                </Stack>
                <SyncFreshness intercomSync={intercomSync} />
              </Stack>

              <ConversationThread
                userId={selected.memberUserId}
                showTags={false}
                triages={triages.triages}
                onOpenTriage={setOpenTriageId}
                statusEvents={statusHistory.events}
              />

              <ReplyComposer
                reply={reply}
                memberUserId={selected.memberUserId}
                disabled={selectedBusy}
                closingTicket={selectedBusy}
                onSnooze={
                  selected.inboxStatus === 'open'
                    ? (until) => snooze(selected.memberUserId, until)
                    : undefined
                }
                onCloseTicket={
                  selected.inboxStatus === 'closed'
                    ? undefined
                    : () =>
                        updateOrToast(selected.memberUserId, {
                          status: 'closed',
                        })
                }
              />
            </>
          )}
        </Stack>

        <ContextRail
          conversation={selected}
          assignees={assignees}
          currentUserId={account?.userId}
          busy={selectedBusy}
          canDeescalate={canDeescalate}
          canSummarize={canSummarize}
          triages={triages}
          openTriageId={openTriageId}
          onSelectTriage={setOpenTriageId}
          onAssign={(conversation, assignedToUserId) =>
            updateOrToast(conversation.memberUserId, { assignedToUserId })
          }
          onOpenNotes={openNotes}
          onRetryAssignees={retryAssigneesIfNeeded}
          onEscalate={(conversation) => setEscalateTarget(conversation)}
          onDeescalate={(conversation) =>
            void applyEscalation(conversation.memberUserId, 'deescalate')
          }
        />
      </Stack>

      <Dialog
        open={!!escalateTarget}
        onClose={() => {
          setEscalateTarget(null);
          setEscalateReason('');
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ color: '#213B54', fontWeight: 600 }}>
          Escalate conversation
          {escalateTarget?.memberName ? ` — ${escalateTarget.memberName}` : ''}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            label="Reason"
            placeholder="Why does this need escalation?"
            value={escalateReason}
            onChange={(event) =>
              setEscalateReason(event.target.value.slice(0, 500))
            }
            sx={{ marginTop: '8px' }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEscalateTarget(null);
              setEscalateReason('');
            }}
            sx={{ color: '#7D818C', textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmEscalate}
            disabled={!escalateReason.trim()}
            variant="contained"
            sx={{
              backgroundColor: '#B42318',
              textTransform: 'none',
              fontWeight: 600,
              ':hover': { backgroundColor: '#912018' },
            }}
          >
            Escalate
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
